import React from "react";
import Input from "../../../../common/Input/Input";
import { useBusinessContext } from "../../../../../context/business";
import numeral from "numeral";
import { BookingPolicy } from "../../BookingHeader";
import CheckBox from "../../../../common/CheckBox/CheckBox";
import moment from "moment";

export default function DetailsForm({
  submitForm,
  currentStepIndex,
  errors,
  useViewDetails,
  isSubmitting 
}) {
  const {
    businessData: { businessData },
    selectedBusiness,
    setSelectedBusiness,
  } = useBusinessContext();

  const { setViewDetails } = useViewDetails();
  const [agreeToPolicy, setAgreeToPolicy] = React.useState(false);
  const [showGuestDetails, setShowGuestDetails] = React.useState(false);
  const [guests, setGuests] = React.useState([]);
  const [countdown, setCountdown] = React.useState(null);
  const [showCountdown, setShowCountdown] = React.useState(false);

  // Check if current booking is a group session with quantity > 1
  const groupService = selectedBusiness.services?.find(s => s.is_group_session);
  const isGroupSession = !!groupService;
  const quantity = groupService?.quantity || 1;
  const guestCount = quantity - 1; // Primary booker is not a guest

  // Initialize guests array when quantity changes and sync to context
  React.useEffect(() => {
    if (isGroupSession && guestCount > 0) {
      const initialGuests = Array(guestCount).fill(null).map((_, index) => ({
        name: '',
        phone: '',
        email: '',
      }));
      setGuests(initialGuests);
      // Send empty guests to context so they're included even if user doesn't interact
      setSelectedBusiness({ type: "UPDATE_USER_DETAILS", data: { guests: initialGuests } });
    } else {
      // Clear guests when not a group session or quantity is 1
      setGuests([]);
      setSelectedBusiness({ type: "UPDATE_USER_DETAILS", data: { guests: undefined } });
    }
  }, [guestCount, isGroupSession]);

  const handleGuestChange = (index, field, value) => {
    const updatedGuests = [...guests];
    updatedGuests[index] = { ...updatedGuests[index], [field]: value };
    setGuests(updatedGuests);
    // Update in context for booking submission
    setSelectedBusiness({ type: "UPDATE_USER_DETAILS", data: { guests: updatedGuests } });
  };

  const handleChange = ({ currentTarget: { value, name } }) => {
    const update = { [name]: value };
    setSelectedBusiness({ type: "UPDATE_USER_DETAILS", data: update });
  };

  const handlePolicyChange = ({ currentTarget }) => {
    if (currentTarget.checked) {
      setAgreeToPolicy(true);
    } else {
      setAgreeToPolicy(false);
    }
  };

  const hasBookingPolicy =
    businessData?.policy && businessData?.policy !== "<p><br></p>";

  // Countdown timer for same-day booking buffer
  React.useEffect(() => {
    const isSameDayBooking = selectedBusiness?.totalCost?.business_settings?.same_day_booking;
    const bufferTimeInSeconds = selectedBusiness?.totalCost?.business_settings?.same_day_booking_time;
    const selectedHour = selectedBusiness?.hour;
    const selectedDate = selectedBusiness?.date;

    if (currentStepIndex === 4 && isSameDayBooking && bufferTimeInSeconds && selectedHour && selectedDate) {
      const updateCountdown = () => {
        const appointmentDate = moment(selectedDate);
        const today = moment();
        const isToday = appointmentDate.isSame(today, 'day');

        if (isToday) {
          const appointmentTime = moment(selectedHour, "hh:mm A");
          const now = moment();
          // Include 2 minutes grace period for payment (same as DateForm)
          const paymentGracePeriod = 2 * 60; // 2 minutes in seconds
          const deadline = appointmentTime.clone().subtract(bufferTimeInSeconds + paymentGracePeriod, 'seconds');
          const timeUntilDeadline = deadline.diff(now, 'seconds');

          // Show countdown if 5 minutes or less remaining
          if (timeUntilDeadline > 0 && timeUntilDeadline <= 5 * 60) {
            const minutes = Math.floor(timeUntilDeadline / 60);
            const seconds = timeUntilDeadline % 60;
            setCountdown({ minutes, seconds });
            setShowCountdown(true);
          } else if (timeUntilDeadline <= 0) {
            setCountdown({ minutes: 0, seconds: 0 });
            setShowCountdown(true);
          } else {
            setShowCountdown(false);
          }
        } else {
          setShowCountdown(false);
        }
      };

      // Update immediately
      updateCountdown();

      // Update every second
      const interval = setInterval(updateCountdown, 1000);

      return () => clearInterval(interval);
    } else {
      setShowCountdown(false);
    }
  }, [currentStepIndex, selectedBusiness]);

  // 2. Determine if the button should be disabled
  const isButtonDisabled = (!agreeToPolicy && hasBookingPolicy) || isSubmitting;

  return (
    <form
      onSubmit={submitForm}
      className="step_container"
      data-is-active={(currentStepIndex === 4).toString()}
    >
      <div className="details_container" required>
        <div className="header flex">
          <p className="heading">Provide your details</p>
        </div>

        {showCountdown && countdown && (
          <div className="same-day-buffer-warning urgent" style={{ marginBottom: '16px' }}>
            <strong>⏱️ Time Remaining:</strong> {countdown.minutes}:{countdown.seconds.toString().padStart(2, '0')}
            {countdown.minutes === 0 && countdown.seconds === 0 ? (
              <span> - This time slot is no longer available. Please select a different time.</span>
            ) : (
              <span> - Complete your booking quickly or this slot may become unavailable.</span>
            )}
          </div>
        )}

        <div className="grid">
          <Input
            labelText="FIRST NAME"
            name="first_name"
            placeholder="Enter first name here"
            onChange={handleChange}
            required
            disabled={isSubmitting} // Optional: disables input during submission
          />
          <Input
            labelText="LAST NAME"
            name="last_name"
            placeholder="Enter last name here"
            onChange={handleChange}
            required
            disabled={isSubmitting}
          />
          <Input
            labelText="PHONE NUMBER"
            type="tel"
            name="phone_number"
            placeholder="e.g. 08034567890"
            onChange={handleChange}
            maxLength="11"
            minLength="11"
            required
            disabled={isSubmitting}
          />
          <Input
            labelText="EMAIL"
            type="email"
            name="email"
            placeholder="e.g. you@example.com"
            onChange={handleChange}
            required
            disabled={isSubmitting}
          />
          {businessData.allow_address && (
            <Input
              labelText="ADDRESS"
              type="text"
              name="address"
              placeholder="Enter your address here"
              onChange={handleChange}
              isTextarea
              rows={5}
              required
              disabled={isSubmitting}
            />
          )}
          {businessData.allow_booking_notes && (
            <Input
              labelText="NOTES (OPTIONAL)"
              type="text"
              name="booking_notes"
              placeholder="Add notes for your booking"
              onChange={handleChange}
              isTextarea
              rows={5}
              disabled={isSubmitting}
            />
          )}

          {selectedBusiness.userDetails?.create_account && (
            <Input
              labelText="PASSWORD"
              type="password"
              name="password"
              placeholder="Create a Password"
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          )}
        </div>

        {errors?.map((error, idx) => (
          <p key={idx} className="form_error">{error.message}</p>
        ))}
      </div>

      {isGroupSession && guestCount > 0 && (
        <div className="guest-details-section">
          <label className="guest-details-checkbox">
            <CheckBox
              checked={showGuestDetails}
              onChange={(e) => setShowGuestDetails(e.currentTarget.checked)}
              disabled={isSubmitting}
            />
            <span className="guest-details-checkbox-text">
              Provide guest details?
              <span className="guest-details-checkbox-optional"> (optional)</span>
            </span>
          </label>

          {showGuestDetails && (
            <div className="guest-details-table">
              <div className="guest-details-header">
                <span className="guest-col-label"></span>
                <span className="guest-col-header">GUEST NAME</span>
                <span className="guest-col-header">PHONE NUMBER</span>
                <span className="guest-col-header">EMAIL ADDRESS</span>
              </div>
              {guests.map((guest, index) => (
                <div key={index} className="guest-details-row">
                  <span className="guest-badge">GUEST {index + 1}</span>
                  <input
                    type="text"
                    placeholder="Enter name"
                    value={guest.name}
                    onChange={(e) => handleGuestChange(index, 'name', e.target.value)}
                    className="guest-input"
                    disabled={isSubmitting}
                  />
                  <input
                    type="tel"
                    placeholder="Enter number"
                    value={guest.phone}
                    onChange={(e) => handleGuestChange(index, 'phone', e.target.value)}
                    className="guest-input"
                    disabled={isSubmitting}
                  />
                  <input
                    type="email"
                    placeholder="Enter email"
                    value={guest.email}
                    onChange={(e) => handleGuestChange(index, 'email', e.target.value)}
                    className="guest-input"
                    disabled={isSubmitting}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {hasBookingPolicy && (
        <div className="booking-policy-agreement">
          <div>
            <CheckBox onChange={handlePolicyChange} disabled={isSubmitting} />
          </div>
          <div>
            <span className="policy-text">
              {`I have read and agree to ${businessData?.name}${
                businessData?.name?.endsWith("s") ? "’" : "’s"
              }`}
            </span>{" "}
            <BookingPolicy triggerType="text" />.
          </div>
        </div>
      )}

      {selectedBusiness?.totalCost?.total_services_cost > 0 ? (
        <div className="footer_rapper">
          <div className="app_max_width flex">
            <div className="details">
              <p>{selectedBusiness?.services?.length || 0} Service(s)</p>
              <p className="flex">
                ₦
                {numeral(
                  selectedBusiness?.totalCost?.total_services_cost
                ).format("0,0.00")}{" "}
                <button
                  className="co_pink"
                  type="button"
                  onClick={() => setViewDetails(true)}
                  disabled={isSubmitting}
                >
                  View details
                </button>
              </p>
            </div>

            {/* 3. Updated Button Logic & UI */}
            <button
              className={`round_button ${isButtonDisabled ? "disabled" : ""}`}
              disabled={isButtonDisabled}
            >
              {isSubmitting ? (
                <span style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ animation: "spin 1s linear infinite" }}
                  >
                    <path fill="currentColor" d="M12 4V2A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8Z"/>
                  </svg>
                  Processing...
                </span>
              ) : (
                "Book Now"
              )}
            </button>
          </div>
        </div>
      ) : null}
    </form>
  );
}